import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import CardDNS from "./CardDNS";
import StyledButton from "../StyledButton";
import { Checkbox } from "@chakra-ui/checkbox";
import { css } from "@emotion/react";
import { GetDomainInfoResponse, MailgunApi, Valid } from "../../api/Mailgun";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { UserAtom } from "../../atoms/UserAtom";
import { DNS_ERROR } from "../Settings/DNSRecords";

const Overlay = styled.div`
  max-width: 100vw;
  width: 100%;
  height: 100vh;
  min-height: 100%;

  background: rgba(0, 0, 0, 0.48);

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 9999;
`;

const ModalBody = styled.div`
  width: 440px;
  height: fit-content;

  padding: 56px 32px;

  background: #ffffff;

  box-shadow: rgba(0, 0, 0, 0.2) 0px 12px 28px 0px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px 0px,
    rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset;

  border-radius: 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;

  .button,
  .button-checked {
    width: 90px;

    margin: 16px 0px;
  }

  .button-checked {
    margin: 16px 0px 0px;
  }

  .title {
    max-width: 280px;

    font-size: 18px;
    font-weight: 600;
    color: #333333;

    margin: 24px 0 16px;
  }

  .description,
  .description-checked {
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    color: #707070;

    margin-bottom: 16px;
  }

  .description-checked {
    margin: 24px 0 16px;
  }

  .image-container {
    width: 112px;
    height: 112px;

    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
    }
  }

  .image-checked-container {
    width: 64px;
    height: 64px;

    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
    }
  }
`;

const CardList = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  grid-gap: 10px;

  margin: 16px 0 40px;
`;

interface VerifyingEmailDNSProps {}

export default function VerifyingEmailDNS({}: VerifyingEmailDNSProps) {
  let [domainInfo, setDomainInfo] = useState<GetDomainInfoResponse>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDNS, setIsLoadingDNS] = useState(true);
  const setUser = useSetRecoilState(UserAtom);
  const [closeModal, setCloseModal] = useState(false);
  const [hasError, setHasError] = useState(true);
  const [isChecked, setIsChecked] = useState(false);
  const [showCheckedDNS, setShowCheckedDNS] = useState(false);

  useEffect(() => {
    if (closeModal) return;

    (async () => {
      const data = await MailgunApi.getDomainInfo();

      if (data?.sending_dns_records.length !== 3) {
        toast("Error to verify your DNS", { type: "error" });
        setDomainInfo(DNS_ERROR);
      } else {
        if (
          data?.sending_dns_records[0].valid === Valid.Valid &&
          data?.sending_dns_records[1].valid === Valid.Valid &&
          data?.sending_dns_records[2].valid === Valid.Valid
        )
          setUser((u) => ({ ...u, domainIsVerified: true }));

        setDomainInfo(data);
      }

      setIsLoadingDNS(false);
    })();
  }, []);

  useEffect(() => {
    if (isLoadingDNS) return;

    setHasError(
      !(
        domainInfo?.sending_dns_records[0].valid === Valid.Valid &&
        domainInfo?.sending_dns_records[1].valid === Valid.Valid &&
        domainInfo?.sending_dns_records[2].valid === Valid.Valid
      )
    );
    setTimeout(() => {
      setIsLoading(false);
    }, 5000);
  }, [isLoadingDNS]);

  function handleOkClick() {
    if (isChecked) {
      setShowCheckedDNS(true);
    } else {
      setCloseModal(true);
    }
  }

  if (closeModal) return;

  if (showCheckedDNS) {
    return (
      <Overlay>
        <ModalBody>
          <div className="image-checked-container">
            <img
              src="/dns-modal/DNSCheck.svg"
              alt="Verifying Email DNS Settings"
            />
          </div>

          <div className="description-checked">
            Okay, no problem. We won’t show you these DNS checks at startup, but
            please know that we will still perform the verification every time
            you log into BerserkerMail and if it fails, you will be notified.
          </div>

          <StyledButton
            className="button-checked "
            onClick={() => setCloseModal(true)}
          >
            Ok
          </StyledButton>
        </ModalBody>
      </Overlay>
    );
  }

  if (isLoading) {
    return (
      <Overlay>
        <ModalBody>
          <div className="image-container">
            <img
              src="/dns-modal/DNSLoading.svg"
              alt="Verifying Email DNS Settings"
            />
          </div>

          <div className="title">Verifying Email DNS Settings.</div>

          <div className="description">
            SPF and DKIM records are very important to your email deliverability
            and the deliverability of the BerserkerMail platform. For that
            reason we verify them at each login.
          </div>

          <CardList>
            <CardDNS
              hasError={
                !!!(domainInfo?.sending_dns_records[0].valid === Valid.Valid)
              }
              isLoading={isLoadingDNS}
              title="SPF"
            />
            <CardDNS
              hasError={
                !!!(domainInfo?.sending_dns_records[1].valid === Valid.Valid)
              }
              isLoading={isLoadingDNS}
              title="DKIM"
            />
            <CardDNS
              hasError={
                !!!(domainInfo?.sending_dns_records[2].valid === Valid.Valid)
              }
              isLoading={isLoadingDNS}
              title="CNAME"
            />
          </CardList>

          <Checkbox
            colorScheme="orange"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
            css={css`
              .css-qlgo61[aria-checked="true"],
              .css-qlgo61[data-checked] {
                background: var(--chakra-colors-orange-50);
                border-color: var(--chakra-colors-orange-50);
                color: var(--chakra-colors-white);
              }
            `}
          >
            Please do not show me this again.
          </Checkbox>
        </ModalBody>
      </Overlay>
    );
  }

  if (!isLoading && hasError) {
    return (
      <Overlay>
        <ModalBody>
          <div className="image-container">
            <img
              src="/dns-modal/DNSError.svg"
              alt="Verifying Email DNS Settings"
            />
          </div>

          <div className="title">
            One or more of the required DNS validations has failed.
          </div>

          <div className="description">
            Please contact the help desk at help@berserkermail.com so that we
            can get this resolved for you. Unfortunately you will not be able to
            send email until the DNS settings have been resolved.
          </div>

          <StyledButton className="button" onClick={handleOkClick}>
            Ok
          </StyledButton>

          <Checkbox
            colorScheme="orange"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
            css={css`
              .css-qlgo61[aria-checked="true"],
              .css-qlgo61[data-checked] {
                background: var(--chakra-colors-orange-50);
                border-color: var(--chakra-colors-orange-50);
                color: var(--chakra-colors-white);
              }
            `}
          >
            Please do not show me this again.
          </Checkbox>
        </ModalBody>
      </Overlay>
    );
  }

  if (!isLoading) {
    return (
      <Overlay>
        <ModalBody>
          <div className="image-container">
            <img
              src="/dns-modal/DNSVerify.svg"
              alt="Verifying Email DNS Settings"
            />
          </div>

          <div className="title">All settings are valid and verified!</div>

          <StyledButton className="button" onClick={handleOkClick}>
            Ok
          </StyledButton>

          <Checkbox
            colorScheme="orange"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
            css={css`
              .css-qlgo61[aria-checked="true"],
              .css-qlgo61[data-checked] {
                background: var(--chakra-colors-orange-50);
                border-color: var(--chakra-colors-orange-50);
                color: var(--chakra-colors-white);
              }
            `}
          >
            Please do not show me this again.
          </Checkbox>
        </ModalBody>
      </Overlay>
    );
  }

  return;
}
